import { useState, useMemo } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import InstallAppButton, { IOSInstallBanner } from "../components/InstallAppButton";

// Phase 13d — Strict validation for the `?redirect=` query param used to
// return to the intended URL after login. Rejects anything that could bounce
// the browser off-origin (protocol-relative `//evil.com`, absolute
// `https://evil.com`, or backslash tricks).
function validateRedirect(raw) {
  if (!raw) return null;
  let decoded;
  try { decoded = decodeURIComponent(raw); } catch { return null; }
  if (!decoded.startsWith("/")) return null;      // must be root-relative
  if (decoded.startsWith("//")) return null;      // protocol-relative → off-origin
  if (decoded.includes("\\")) return null;        // backslash tricks
  if (/^https?:/i.test(decoded)) return null;     // full URL after decode
  return decoded;
}

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const validatedRedirect = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return validateRedirect(params.get("redirect"));
  }, [location.search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="loading-screen">
        Loading…
      </div>
    );
  }
  if (user) {
    // Prefer the validated ?redirect=... over the older state.from.pathname,
    // since the query param preserves the full path+search+hash.
    const from = validatedRedirect || location.state?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      navigate(validatedRedirect || "/", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center">
          <img
            src="/logo.png"
            alt="ExpenseReconcile"
            className="h-16 md:h-20 w-auto"
            data-testid="login-brand-logo"
          />
        </div>
        <IOSInstallBanner />
        <Card className="border-slate-200 shadow-sm" data-testid="login-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold text-slate-900">Sign in</CardTitle>
            <CardDescription className="text-slate-500">
              Enter your work email and password to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />
              </div>
              {error && (
                <Alert variant="destructive" data-testid="login-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-btn"
                className="w-full bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
        <div className="flex justify-center">
          <InstallAppButton variant="login" />
        </div>
      </div>
    </div>
  );
}
